import React, { useState } from 'react';
import { Form, Button, Typography, Input, Row, Col } from 'antd';
import { MessageCircle } from 'lucide-react';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface FeedbackRatingFormProps {
  onSubmit: (data: { rating: number; comments?: string }) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

const ratings = [
  { value: 1, emoji: '😞', label: 'Very Dissatisfied', color: 'text-red-500' },
  { value: 2, emoji: '😐', label: 'Dissatisfied', color: 'text-orange-500' },
  { value: 3, emoji: '🙂', label: 'Neutral', color: 'text-yellow-500' },
  { value: 4, emoji: '😊', label: 'Satisfied', color: 'text-green-500' },
  { value: 5, emoji: '🤩', label: 'Very Satisfied', color: 'text-blue-500' },
];

const FeedbackRatingForm: React.FC<FeedbackRatingFormProps> = ({
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comments, setComments] = useState('');

  const handleRatingSelect = (rating: number) => {
    setSelectedRating(rating);
  };

  const handleSubmit = () => {
    if (selectedRating !== null) {
      onSubmit({
        rating: selectedRating,
        comments: comments.trim() || undefined,
      });
    }
  };

  return (
    <div className="h-full flex flex-col max-w-3xl mx-auto">
      <div className="text-center mb-4">
        <Title level={3} className="text-gray-800 mb-1">
          Rate Your Experience
        </Title>
        <Text className="text-gray-600 text-base">
          How would you rate your overall experience with us?
        </Text>
      </div>

      <div className="flex-1 flex flex-col">
        {/* Rating Selection */}
        <div className="mb-4">
          <Row gutter={[12, 12]} justify="center">
            {ratings.map((rating) => (
              <Col key={rating.value} xs={8} sm={6} md={4}>
                <div
                  className={`
                    cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 text-center
                    ${
                      selectedRating === rating.value
                        ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }
                  `}
                  onClick={() => handleRatingSelect(rating.value)}
                >
                  <div className="text-4xl mb-2">{rating.emoji}</div>
                  <div className={`font-medium text-xs ${rating.color}`}>
                    {rating.label}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Selected Rating Display */}
        {selectedRating && (
          <div className="text-center mb-4 p-3 bg-blue-50 rounded-lg">
            <Text className="text-base">
              You selected:{' '}
              <span className="font-bold text-blue-600">
                {ratings.find(r => r.value === selectedRating)?.label}
              </span>
            </Text>
          </div>
        )}

        {/* Comments Section */}
        <div className="flex-1 mb-4">
          <Form layout="vertical">
            <Form.Item
              label={
                <span className="text-base font-medium flex items-center gap-2">
                  <MessageCircle size={18} className="text-gray-400" />
                  Additional Comments <Text className="text-gray-400 text-xs">(Optional)</Text>
                </span>
              }
            >
              <TextArea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Tell us more about your experience..."
                rows={3}
                className="rounded-lg"
                style={{ fontSize: '14px' }}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 mt-auto">
          <Button
            size="large"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-8 py-2 h-auto rounded-lg"
          >
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={selectedRating === null}
            loading={isSubmitting}
            className="px-12 py-2 h-auto rounded-lg bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
            style={{ fontSize: '16px' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackRatingForm;